import {
  AllocationValueTypeEnum,
  formattedDateForSubscription,
  logger,
  NEW_ORDER_SINGLE,
  NotificationVariants,
  OrdStatusEnum,
  OrdTypeEnum,
  QuoteStatusEnum,
  type Allocation,
  type AllocationBE,
  type CareOrder,
  type ExecutionReport,
  type ExpressedOutcomeEnum,
  type IWebSocketClient,
  type ParentOrderTypeEnum,
  type PricingModeEnum,
  type Quote,
  type QuoteMarket,
  type Security,
  type SideEnum,
  type SubscriptionResponse,
} from '@talos/kyoko';
import type { Observable } from 'rxjs';
import { v1 as uuid } from 'uuid';
import type { AvaAcceptQuoteRequest } from '../../../providers/quotes.types';
import { DatadogTradingAction, trackQuoteAction } from '../../../utils/datadogTradingActions';
import { assertOrderCurrency } from '../../../utils/order';
import { resolveUiRequestSource } from '../../../utils/resolveUiRequestSource';
import { getOrdTypeForQuote } from '../../../utils/rfq';
import { isCareOrWorkingOrderParentType } from './tradeCareOrder';
import { getOrderQtyForQuoteAcceptance } from './utils';

const ACCEPT_QUOTE_TIMEOUT_MS = 5_000;

/**
 * Shared decision logic for resolving which SubAccount or Allocation to attach to
 * a quote-related request (both requesting and accepting quotes).
 */
export function resolveSubAccountOrAllocation({
  action,
  careOrderSubAccount,
  allocations,
  allocationValueType,
  useTradeAllocations,
}: {
  action: 'request-quote' | 'accept-quote';
  careOrderSubAccount: string | undefined;
  allocations: Allocation[];
  allocationValueType: AllocationValueTypeEnum;
  useTradeAllocations: boolean;
}): { SubAccount?: string; Allocation?: AllocationBE } {
  // If the Quote has an associated sub account, use that. (And make sure its the same SA that is set for the quote... should always be)
  if (careOrderSubAccount != null && careOrderSubAccount === allocations.at(0)?.subAccount) {
    return { SubAccount: careOrderSubAccount };
  }

  if (useTradeAllocations && allocations.length > 0) {
    if (action === 'request-quote') {
      return {
        Allocation: {
          ValueType: allocationValueType,
          Allocations: allocations.map(a => ({
            SubAccount: a.subAccount,
            Value: a.value,
          })),
        },
      };
    }
    // accept-quote: backend grabs allocations from the Quote
    return {};
  }

  if (allocations.length === 1) {
    return { SubAccount: allocations[0].subAccount };
  }

  return {};
}

export interface AcceptQuoteParams {
  childQuote: QuoteMarket;
  currentQuote: Quote;
  side: string;
  price: string;
  ordType: OrdTypeEnum;
  tradeBest: boolean;
  allowedSlippage?: string;

  // Resolved form values
  symbol: string;
  orderQty: string;
  /** Request currency string; RFQ unit/contract quantities should pass undefined. */
  currency: string | undefined;
  group?: string;
  pricingMode?: PricingModeEnum;
  expressedOutcome?: ExpressedOutcomeEnum;

  // For currency validation
  security: Security;

  // Care order association. Pass undefined explicitly when the quote is not coupled to a care order.
  associatedCareOrder: CareOrder | undefined;
  parentOrderType: ParentOrderTypeEnum | undefined;

  // Sub-account resolution inputs
  useTradeAllocations: boolean;
  allocations: Allocation[];
  allocationValueType: AllocationValueTypeEnum;
  truncateCareOrderID: boolean;
}

export interface AcceptQuoteDeps {
  webSocketClient: IWebSocketClient<unknown>;
  executionReportsSub: Observable<SubscriptionResponse<ExecutionReport>>;
  isMobileLayout?: boolean;
}

/**
 * Result of the acceptQuote flow. Resolves when the matching execution report is non-rejected.
 * Rejects when publication fails, the matching execution report is rejected, or no feedback arrives in time.
 */
export type AcceptQuoteFailure = { text: string; variant?: NotificationVariants };
export type AcceptQuoteResult = { executionReport: ExecutionReport };

/**
 * Builds `AcceptQuoteParams` from a `Quote` entity and user-action inputs.
 *
 * Derives as much as possible from the Quote itself:
 * - `symbol`, `orderQty`, `currency`, `group`, `pricingMode` from Quote fields
 * - `allocations`, `allocationValueType`, `useTradeAllocations` from `Quote.Allocation` / `Quote.SubAccount`
 * - `associatedCareOrder` must be passed explicitly; use `quote.careOrder` for enriched quotes
 *
 * The caller only needs to provide the user-action inputs (`side`, `price`, `tradeBest`)
 * plus `security` which requires external context to resolve.
 *
 * `ordType` is derived internally from the security type and the Quote's pricing mode.
 *
 * Returns `undefined` if the Quote is missing required values (e.g. `OrderQty`).
 */
export function quoteToAcceptQuoteParams({
  quote,
  childQuote,
  side,
  price,
  tradeBest,
  allowedSlippage,
  orderQty: orderQtyOverride,
  truncateCareOrderID,
  security,
  associatedCareOrder,
}: {
  quote: Quote;
  childQuote: QuoteMarket;
  side: SideEnum;
  price: string;
  tradeBest: boolean;
  // AllowedSlippage is only relevant for OrdType LimitAllIn (streaming RFQs)
  // TODO this will be implemented in a coming PR (Olof)
  allowedSlippage?: string;
  /** Explicit accept quantity from partial-fill/sweeper flows. */
  orderQty?: string;
  truncateCareOrderID: boolean;
  security: Security;
  /** Pass `quote.careOrder` for enriched quotes, or `undefined` when no care order is associated. */
  associatedCareOrder: CareOrder | undefined;
}): AcceptQuoteParams | undefined {
  if (!quote.OrderQty) {
    return undefined;
  }

  const orderQty = getOrderQtyForQuoteAcceptance({
    side,
    childQuote,
    quoteOrderQty: quote.OrderQty,
    explicitOrderQty: orderQtyOverride,
    associatedCareOrder,
  });

  const ordType = getOrdTypeForQuote(security, quote.PricingMode, quote.RFQTradingProtocol);

  return {
    childQuote,
    currentQuote: quote,
    side,
    price,
    ordType,
    tradeBest,
    allowedSlippage,
    symbol: quote.Symbol,
    orderQty,
    currency: quote.Currency,
    group: quote.Group,
    pricingMode: quote.PricingMode,
    expressedOutcome: quote.ExpressedOutcome,
    security,
    associatedCareOrder,
    parentOrderType: quote.ParentOrderType ?? associatedCareOrder?.myParentOrderType,
    useTradeAllocations: quote.Allocation != null,
    allocations: getAllocationsFromQuote(quote),
    allocationValueType: quote.Allocation?.ValueType ?? AllocationValueTypeEnum.Percentage,
    truncateCareOrderID,
  };
}

/** Extracts an `Allocation[]` from a Quote's backend allocation or sub-account fields */
function getAllocationsFromQuote(quote: Quote): Allocation[] {
  if (quote.Allocation) {
    return quote.Allocation.Allocations.map(a => ({
      subAccount: a.SubAccount,
      value: a.Value,
    }));
  }

  if (quote.SubAccount) {
    return [{ subAccount: quote.SubAccount, value: '1' }]; // backend's "1" = 100%
  }

  return [];
}

/**
 * Standalone, redux-free version of the accept-quote logic.
 * Builds an `AvaAcceptQuoteRequest`, validates currency, and publishes via websocket.
 *
 * Returns a promise. On rejection, `text` contains a user-facing message.
 * Callers are responsible for showing toasts.
 */
export async function acceptQuote(params: AcceptQuoteParams, deps: AcceptQuoteDeps): Promise<AcceptQuoteResult> {
  const {
    childQuote,
    currentQuote,
    side,
    price,
    ordType,
    tradeBest,
    allowedSlippage,
    symbol,
    orderQty,
    currency,
    group,
    pricingMode,
    expressedOutcome,
    security,
    associatedCareOrder,
    parentOrderType,
    useTradeAllocations,
    allocations,
    allocationValueType,
    truncateCareOrderID,
  } = params;
  const { webSocketClient, executionReportsSub, isMobileLayout = false } = deps;

  const clOrdID = uuid();
  const marketAccountNames: string[] = currentQuote.Markets.filter(
    quote => quote.QuoteStatus === QuoteStatusEnum.Open
  ).map(quote => quote.MarketAccount);

  const request: AvaAcceptQuoteRequest = {
    ClOrdID: clOrdID,
    RFQID: currentQuote.RFQID,
    Symbol: symbol,
    Side: side,
    Price: price,
    OrdType: ordType,
    Currency: currency,
    OrderQty: orderQty,
    Group: group ?? '',
    Markets: tradeBest ? marketAccountNames : [childQuote.MarketAccount],
    TransactTime: formattedDateForSubscription(new Date()),
    PricingMode: pricingMode,
  };
  if (expressedOutcome != null) {
    request.ExpressedOutcome = expressedOutcome;
  }

  const requestSource = resolveUiRequestSource({ isMobileLayout });
  if (requestSource) {
    request.RequestSource = requestSource;
  }

  if (associatedCareOrder) {
    if (isCareOrWorkingOrderParentType(parentOrderType)) {
      request.ParentOrderID = associatedCareOrder.OrderID;
      request.ParentOrderType = parentOrderType;
    }

    request.Comments = getAvaAcceptQuoteRequestCareOrderComment(associatedCareOrder, truncateCareOrderID);

    if (associatedCareOrder.SettleDate) {
      request.SettleDate = associatedCareOrder.SettleDate;
    }
  }

  // If the OrdType is RFQ (happens for StF and Compound quotes), we need to pass a specific account, as well as the exact QuoteID.
  if (request.OrdType === OrdTypeEnum.RFQ) {
    request.Markets = [childQuote.MarketAccount];
    request.QuoteID = currentQuote.QuoteID;
  }

  if (request.OrdType === OrdTypeEnum.LimitAllIn) {
    // AllowedSlippage only applicable in this case
    request.AllowedSlippage = allowedSlippage;
  }

  const resolved = resolveSubAccountOrAllocation({
    action: 'accept-quote',
    careOrderSubAccount: associatedCareOrder?.SubAccount,
    allocations,
    allocationValueType,
    useTradeAllocations,
  });

  // When accepting a quote, we only attach a sub account, never allocations.
  // If we're trading a Quote with Allocations, the backend will grab the Allocations from the Quote and put on the Order for us.
  if (resolved.SubAccount) {
    request.SubAccount = resolved.SubAccount;
  }

  if (!assertOrderCurrency(request, security)) {
    return Promise.reject({
      text: 'Mismatch in order currency found while accepting the quote. Please contact Talos support.',
    });
  }

  const cleanups: (() => void)[] = [];

  // Accepting a quote can fail for three reasons: 1) NewOrderSingle is Rejected (eg markets offline),
  // 2) NewOrderSingle publication errors (eg bad request payload, backend fails to parse), or 3) timeout (we dont hear anything back)
  // Each of these is represented by one promise below. The promises are in a race. Whichever fails first will be bubbled up.
  // The success condition is if we get a non-Rejected (and non-PendingNew) ER. That proves that the Order was placed.
  const executionReportPromise = new Promise<AcceptQuoteResult>((resolve, reject) => {
    const subscription = executionReportsSub.subscribe(response => {
      const executionReport = response.data.findLast(report => report.ClOrdID === clOrdID);
      if (executionReport == null) {
        return;
      }

      if (executionReport.OrdStatus === OrdStatusEnum.PendingNew) {
        // PendingNew branches out to either Rejected or New. So if we get PendingNew we just return early (and wait for the next update to do anything)
        return;
      }

      if (executionReport.OrdStatus === OrdStatusEnum.Rejected) {
        reject({
          text: executionReport.Text || 'Order rejected with unknown reason. Please contact Talos support.',
          variant: NotificationVariants.Warning,
        });
        return;
      }

      // Else our Order was accepted and we can report back a successful status.
      resolve({ executionReport });
    });

    cleanups.push(() => subscription.unsubscribe());
  });

  const publicationErrorPromise = new Promise<never>((_, reject) => {
    trackQuoteAction(DatadogTradingAction.AcceptQuote, { clOrdID: request.ClOrdID });
    const cleanupPublication = webSocketClient.registerPublication(
      {
        type: NEW_ORDER_SINGLE,
        data: [request],
      },
      err => {
        logger.error(new Error(`NewOrderSingle publication returned an error (acceptQuote)`), {
          extra: { error: err },
        });

        reject({
          text: `Error: ${err.error?.msg ?? 'Unknown error'}`,
        });
      }
    );

    cleanups.push(cleanupPublication);
  });

  const timeoutPromise = new Promise<never>((_, reject) => {
    const timeoutId = setTimeout(() => {
      reject({
        text: 'Timed out waiting for order confirmation. Please check the order status.',
      });
    }, ACCEPT_QUOTE_TIMEOUT_MS);

    cleanups.push(() => clearTimeout(timeoutId));
  });

  return Promise.race([executionReportPromise, publicationErrorPromise, timeoutPromise]).finally(() => {
    cleanups.forEach(cleanup => cleanup());
  });
}

/** Object defining what stuff we shove into the Comments (via JSON.stringify) */
interface QuoteCareOrderCommentJSON {
  PostTradeClearingSymbol?: string;
  PostTradeClearingID: string;
}

function getPostTradeClearingID(careOrderID: string, truncateCareOrderID: boolean): string {
  return truncateCareOrderID ? careOrderID.replaceAll('-', '') : careOrderID;
}

export function getAvaAcceptQuoteRequestCareOrderComment(careOrder: CareOrder, truncateCareOrderID = false): string {
  const commentJson: QuoteCareOrderCommentJSON = {
    PostTradeClearingID: getPostTradeClearingID(careOrder.OrderID, truncateCareOrderID),
  };
  if (careOrder.fund) {
    commentJson.PostTradeClearingSymbol = careOrder.fund;
  }

  return JSON.stringify(commentJson);
}
